<template>
    <hts-drawer
        v-model="visible"
        :title="$t('labels.documentList')"
        :size="60"
        @close="closeOverSide">
        <div class="mb-3 text-right">
            <hts-form-button
                v-if="checkPermission(layoutPermissions.vehicle_document_add)"
                color="main"
                icon-name="hi-plus-circle-icon"
                icon-position="right"
                type="submit"
                @click="handleAddDocument">
                {{ $t('labels.addDocument') }}
            </hts-form-button>
        </div>
        <hts-data-table
            ref="dataDocument"
            :headers="document"
            :server="server"
            :uri-params="uriParams"
            :detail-row-auto-height="true"
            :use-archive="apiArchive"
            :use-restore="apiRestore"
            use-selection
            store-name="vehicleDataTable"
            store-key="VehicleDocument" />
        <hts-modal
            :show="showPopupDeleteDocument"
            :title="$t('labels.deleteDocument')"
            type="confirm"
            :content="$t('labels.deleteDocumentContent')"
            @hide="closePopupDeleteDocument"
            @confirm="() => actionDeleteDocument()" />
        <edit-document
            ref="showDrawerEditDocument"
            :document-info="documentSelected"
            :type="props.type"
            @refreshDataTables="refreshTables()" />
        <add-document
            ref="addDocument"
            type="vehicle"
            @refreshDataTables="refreshTables()" />
    </hts-drawer>
</template>

<script lang="ts" setup>
import Actions from '../actions.vue'
import AddDocument from './add-document.vue'
import EditDocument from './edit-document.vue'
import { checkPermission } from '@/utils/permission-check'
import layoutPermissions from '@/data/permissions/layout-permissions'
const { $formatters } = useNuxtApp()

const props = defineProps({
  color: {
    type: String,
    default: 'theme'
  },
  sizeSlideOver: {
    type: String,
    default: 'screen-lg'
  },
  column: {
    type: Number,
    default: 3
  },
  type: {
    type: String,
    default: ''
  },
  reload: {
    type: Boolean,
    default: false
  }
})
const visible = ref(false)
const server = ref()
const codeVehicle = ref()
const uriParams = reactive({
  ID: ''
})
const openDrawerDocument = (idVehicle: string) => {
  codeVehicle.value = idVehicle
  uriParams.ID = idVehicle
  visible.value = true
  if (props.reload) {
    refreshTables()
  }
  server.value = $urlPartner.vehicle.vehicle.getDocuments
}
defineExpose({
  openDrawerDocument
})
const closeOverSide = () => {
  visible.value = false
}
const handleEditDocument = (document: any) => {
  documentSelected.value = document
  if (showDrawerEditDocument.value) {
    showDrawerEditDocument.value.openEditDocument()
  }
}
const handleDeleteDocument = (document: any) => {
  showPopupDeleteDocument.value = true
  Object.assign(documentIsSelectedForDelete, document)
}

const handleAddDocument = () => {
  if (addDocument.value) {
    addDocument.value.openAddDocument(codeVehicle.value)
  }
}
const document = [
  {
    field: 'type',
    headerName: $t('labels.documentType'),
    sortable: true,
    valueFormatter: (params: any) => {
      if (params.data.type === 'INSURANCE') {
        return $t('labels.insurance')
      } else if (params.data.type === 'OTHER') {
        return $t('labels.other')
      } else {
        return $t('labels.registry')
      }
    }
  },
  {
    field: 'fileName',
    headerName: $t('labels.documentFileName'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'insurancePrice',
    headerName: $t('labels.insurancePriceByYear'),
    sortable: true,
    valueFormatter: (params: any) => {
      if (!params.data.insurancePrice) {
        return ''
      }
      return $formatters.formatCurrency(params.data.insurancePrice)
    }
  },
  {
    field: 'expiredDate',
    headerName: $t('labels.expiredDate'),
    sortable: true,
    valueFormatter: (params: any) => {
      if (!params.data.expiredDate) {
        return ''
      }
      return $formatters.date(params.data.expiredDate)
    }
  },
  {
    field: 'path',
    headerName: $t('labels.link'),
    sortable: true,
    valueFormatter: (params: any) => {
      return $formatters.cdnUrl(params.data)
    },
    onCellClicked: (params: any) => {
      window.open($formatters.cdnUrl(params.data))
    }
  },
  {
    field: 'description',
    headerName: $t('labels.description'),
    sortable: true
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.createdAt)
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: Actions,
    cellRendererParams: {
      storeKey: 'vehicleDataTable',
      storeName: 'VehicleDocument',
      items: [
        {
          icon: 'hi-pencil-icon',
          label: $t('labels.edit'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.vehicle_document_edit),
          click: handleEditDocument
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-600 hover:text-error-500',
          permissions: checkPermission(layoutPermissions.vehicle_document_delete),
          click: handleDeleteDocument,
          isArchive: true
        }
      ]
    }
  }
]

const showPopupDeleteDocument: any = ref(false)
const showDrawerEditDocument: any = ref(false)
const addDocument = ref()
const documentIsSelectedForDelete = reactive<{ [key: string]: any }>({})
const documentSelected = ref({})
const dataDocument = ref()

const refreshTables = () => {
  if (dataDocument.value) {
    dataDocument.value.refresh()
  }
}

const closePopupDeleteDocument = () => {
  showPopupDeleteDocument.value = false
  Object.assign(documentIsSelectedForDelete, {})
}

const actionDeleteDocument = () => {
  const api =
    props.type === 'vehicle'
      ? {
        url: '/vehicle/document/delete',
        method: 'DELETE'
      }
      : {
        url: '/vehicle/remooc-document/delete',
        method: 'DELETE'
      }
  $api(api, { body: { ids: [documentIsSelectedForDelete._id], vehicle: codeVehicle.value } }).then((data) => {
    const resData: any = data?.data.value

    if (resData.success) {
      Object.assign(documentIsSelectedForDelete, {})
      refreshTables()
      showPopupDeleteDocument.value = false
      process.client && $toast().success($t('labels.deleteSuccess'))
    }
  })
}

const apiArchive = computed(() => {
  return { api: $urlPartner.vehicle.vehicle.archiveDocument, permissions: checkPermission(layoutPermissions.vehicle_document_archive) }
})

const apiRestore = computed(() => {
  return { api: $urlPartner.vehicle.vehicle.restoreDocument, permissions: checkPermission(layoutPermissions.vehicle_document_restore) }
})
</script>
